// PredefinedQADoubtChat.js - Floating chat for predefined Q&A doubts
import React, { useState, useRef, useEffect } from 'react';
import ApiService from '../services/api';
import { formatTextContent } from '../utils/textformatting';
import '../utils/textformatting.css';
import './PredefinedQADoubtChat.css';

const PredefinedQADoubtChat = ({ 
  qaId,
  subjectId, 
  chapterId, 
  topicId,
  subtopicId,
  language,
  onClose 
}) => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [followUpCount, setFollowUpCount] = useState(0);
  const [limitReached, setLimitReached] = useState(false);
  const messagesEndRef = useRef(null);

  // Get student ID
  const userData = JSON.parse(localStorage.getItem('userData'));
  const studentId = userData?.student?.student_id;

  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputText.trim() || isLoading || limitReached) return;

    const userMessage = {
      id: Date.now(),
      text: inputText,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);

    try {
      const response = await ApiService.askPredefinedQADoubt(
        inputText,
        sessionId,
        studentId,
        qaId,
        subjectId,
        chapterId,
        topicId,
        subtopicId,
        language
      );

      // Update session ID if new
      if (response.session_id && !sessionId) {
        setSessionId(response.session_id);
      }

      // Update follow-up tracking
      setFollowUpCount(response.follow_up_count);
      setLimitReached(response.follow_up_limit_reached);

      // Add bot response
      const botMessage = {
        id: Date.now() + 1,
        text: response.answer,
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);

    } catch (error) {
      console.error('Error asking doubt:', error);
      
      // Check if it's the 429 (limit reached) error
      if (error.response && error.response.status === 429) {
        setLimitReached(true);
      }
      
      const errorMessage = {
        id: Date.now() + 1,
        text: language === 'hi' 
          ? "खुशी है कि आपने सवाल पूछा! कुछ तकनीकी समस्या है, कृपया दोबारा कोशिश करें।"
          : "Sorry, I encountered an error. Please try again.",
        sender: 'bot',
        timestamp: new Date(),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
    }

    setIsLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setSessionId(null);
    setFollowUpCount(0);
    setLimitReached(false);
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="qa-doubt-chat-container">
      {/* Header */}
      <div className="qa-doubt-header">
        <span className="qa-doubt-title">❓ Ask Your Doubt</span>
        <button className="qa-doubt-close" onClick={onClose}>✕</button>
      </div>

      {/* Messages Container */}
      <div className="qa-doubt-messages">
        {messages.length === 0 ? (
          <div className="qa-doubt-welcome">
            <p>{language === 'hi' ? "अपना सवाल पूछें! 💬" : "Ask your question! 💬"}</p>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`qa-doubt-message ${message.sender}`}
            >
              <div className="qa-doubt-message-content">
                {formatTextContent(message.text)}
              </div>
              <span className="qa-doubt-time">{formatTime(message.timestamp)}</span>
            </div>
          ))
        )}
        
        {isLoading && (
          <div className="qa-doubt-message bot">
            <div className="qa-doubt-message-content">
              <span className="qa-doubt-loading">🤔 {language === 'hi' ? 'सोच रहा हूँ...' : 'Thinking...'}</span>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="qa-doubt-input-area">
        {limitReached && (
          <div className="qa-doubt-limit-notification">
            <span>⚠️</span>
            <span>
              {language === 'hi'
                ? 'फॉलो-अप सीमा पूर्ण। नया प्रश्न शुरू करें।'
                : 'Follow-up limit reached. Start new question.'}
            </span>
          </div>
        )}
        
        <div className="qa-doubt-input-row">
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={
              limitReached
                ? (language === 'hi' ? "सीमा पूर्ण" : "Limit reached")
                : language === 'hi' 
                ? "अपना सवाल टाइप करें..."
                : "Type your question..."
            }
            disabled={isLoading || limitReached}
            className="qa-doubt-textarea"
            rows={2}
          />
          
          <button
            onClick={handleSendMessage}
            disabled={!inputText.trim() || isLoading || limitReached}
            className="qa-doubt-send-btn"
          >
            ➤
          </button>
        </div>
        
        {messages.length > 0 && (
          <button onClick={clearChat} className="qa-doubt-clear-btn">
            {language === 'hi' ? '🗑️ साफ़ करें' : '🗑️ Clear Chat'}
          </button>
        )}
      </div>
    </div>
  );
};

export default PredefinedQADoubtChat;